import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { CoverImage } from '../components/CoverImage';
import type { Series, SeriesFormData } from '../services/types';
import { SeriesEditorModal } from '../components/admin/SeriesEditorModal';

export function SeriesListPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [series, setSeries] = useState<Series[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 編輯 Modal 狀態
  const [showEditor, setShowEditor] = useState(false);
  const [editingSeries, setEditingSeries] = useState<Series | null>(null);

  // 載入系列列表
  useEffect(() => {
    loadSeries();
  }, []);

  const loadSeries = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.getSeries();
      setSeries(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSeries = () => {
    setEditingSeries(null);
    setShowEditor(true);
  };

  const handleEditSeries = (s: Series) => {
    setEditingSeries(s);
    setShowEditor(true);
  };

  const handleSaveSeries = async (data: SeriesFormData) => {
    if (editingSeries) {
      const updated = await api.updateSeries(editingSeries.id, data);
      setSeries(prev => prev.map(s => s.id === updated.id ? { ...s, ...updated } : s));
    } else {
      const created = await api.createSeries(data);
      setSeries(prev => [created, ...prev]);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        {/* Header - Editorial 風格 */}
        <header className="mb-10 sm:mb-14">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="heading-display">小說系列</h1>
              <p className="body-base mt-2">探索精心策劃的作品合集</p>
            </div>

            {isAdmin && (
              <button
                onClick={handleCreateSeries}
                className="btn btn-primary btn-md"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                新增系列
              </button>
            )}
          </div>
        </header>

        {/* Error State */}
        {error && (
          <div className="mb-8 p-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800/50 text-danger-700 dark:text-danger-300 rounded-card flex items-center gap-3">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
            <button onClick={loadSeries} className="ml-auto text-sm font-medium hover:underline">
              重試
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-24 sm:py-32">
            <div className="relative">
              <div className="w-12 h-12 border-3 border-neutral-200 dark:border-neutral-700 rounded-full" />
              <div className="absolute inset-0 w-12 h-12 border-3 border-transparent border-t-primary-500 rounded-full animate-spin" />
            </div>
            <p className="mt-4 body-small">載入中...</p>
          </div>
        ) : series.length === 0 ? (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-20 sm:py-28 text-center">
            <div className="relative mb-6">
              {/* 簡化的書籍插圖 */}
              <div className="relative w-32 h-40 flex items-center justify-center">
                <div className="absolute w-16 h-24 bg-neutral-200 dark:bg-neutral-700 rounded shadow-sm transform -rotate-6 translate-x-2" />
                <div className="absolute w-16 h-24 bg-neutral-300 dark:bg-neutral-600 rounded shadow-sm transform rotate-3 -translate-x-1" />
                <div className="absolute w-16 h-24 bg-primary-100 dark:bg-primary-900/30 rounded shadow-sm transform rotate-6 translate-x-4" />
              </div>
            </div>
            <h3 className="heading-section mb-2">還沒有任何系列</h3>
            <p className="body-base max-w-sm mb-6">
              {isAdmin ? '建立你的第一個系列，將相關作品整理在一起' : '管理員正在整理精選系列，敬請期待'}
            </p>
            {isAdmin && (
              <button onClick={handleCreateSeries} className="btn btn-primary btn-md">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                建立第一個系列
              </button>
            )}
          </div>
        ) : (
          /* Series Grid */
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
            {series.map((s) => (
              <SeriesCard
                key={s.id}
                series={s}
                onEdit={() => handleEditSeries(s)}
                showEditButton={isAdmin}
              />
            ))}
          </div>
        )}
      </div>

      {/* Editor Modal */}
      <SeriesEditorModal
        series={editingSeries}
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={handleSaveSeries}
      />
    </div>
  );
}

// 系列卡片元件
interface SeriesCardProps {
  series: Series;
  onEdit?: () => void;
  showEditButton?: boolean;
}

function SeriesCard({ series, onEdit, showEditButton = false }: SeriesCardProps) {
  return (
    <Link
      to={`/series/${series.id}`}
      className="card-interactive group overflow-hidden"
    >
      {/* Cover Area */}
      <div className="relative aspect-[3/4] bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
        <CoverImage
          src={series.cover_url}
          alt={series.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          fallback={
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div className="relative flex items-center justify-center">
                {series.novel_count > 0 ? (
                  [...Array(Math.min(series.novel_count, 3))].map((_, i) => (
                    <div
                      key={i}
                      className="w-5 h-28 rounded-sm shadow-sm -ml-2 first:ml-0 transition-transform duration-300 group-hover:translate-x-0.5"
                      style={{
                        backgroundColor: `hsl(30, ${15 - i * 3}%, ${72 - i * 8}%)`,
                        transform: `rotate(${(i - 1) * 3}deg)`,
                      }}
                    />
                  ))
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center">
                    <span className="text-2xl font-semibold text-neutral-500 dark:text-neutral-400">
                      {series.title.charAt(0)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          }
        />

        {/* Novel count badge */}
        <div className="absolute top-3 right-3">
          <span className="badge badge-neutral backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            {series.novel_count}
          </span>
        </div>

        {/* Edit button */}
        {showEditButton && onEdit && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEdit();
            }}
            className="absolute top-3 left-3 p-2 bg-white/90 dark:bg-neutral-800/90 backdrop-blur-sm rounded-button shadow-sm opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-white dark:hover:bg-neutral-700"
          >
            <svg className="w-4 h-4 text-neutral-600 dark:text-neutral-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        )}
      </div>

      {/* Info Area */}
      <div className="p-4">
        <h3 className="font-medium text-neutral-900 dark:text-neutral-50 truncate group-hover:text-primary-500 transition-colors">
          {series.title}
        </h3>
        {series.author && (
          <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate mt-0.5">
            {series.author}
          </p>
        )}
      </div>
    </Link>
  );
}
